/**
 * PII redaction (spec 68 §8.1).
 *
 * Every log attribute + Amplitude property passes through `scrubAttributes`
 * before it leaves the process. The rules, in the exact precedence order
 * they are applied to a value:
 *
 *   | # | Rule                                              | Output              | pii() override? |
 *   |---|---------------------------------------------------|---------------------|-----------------|
 *   | 5 | key ∈ {password, secret, token, api_key, …}       | entry DROPPED       | no (compliance) |
 *   | 4 | value ~ `Bearer …` or `c0a_…`                     | <redacted-token>    | no (compliance) |
 *   | 3 | value ~ credit-card (13–19 digits, Luhn)          | <redacted-cc>       | no (compliance) |
 *   | 2 | value ~ RFC-5322 email                            | <redacted-email>    | yes             |
 *   | 1 | key ∈ {email, phone, address, name}               | <redacted>          | yes             |
 *   | 6 | nested object depth > 5                           | <redacted-depth>    | n/a (truncate)  |
 *
 * The compliance carve-outs (rules 3, 4, 5) fire even when the value is
 * wrapped in `pii()` — a Bearer token or a card number is never sent, no
 * matter how the call site marked it.
 */

import type { Attributes } from '../core/types.js';
import { isPii, unwrapPii } from '../pii.js';

export const REDACTED = '<redacted>';
export const REDACTED_EMAIL = '<redacted-email>';
export const REDACTED_CC = '<redacted-cc>';
export const REDACTED_TOKEN = '<redacted-token>';
export const REDACTED_DEPTH = '<redacted-depth>';

const MAX_DEPTH = 5;

// Rule 5 — object keys whose entire entry is dropped (not even the key
// name survives). Matched against a normalised (lowercased, delimiters
// stripped) key so `api_key`, `apiKey`, `API-KEY` all hit `apikey`.
const DROP_KEY_SUBSTRINGS = ['password', 'secret', 'apikey', 'clientsecret'] as const;

// Rule 1 — keys whose value is replaced with <redacted> unless pii().
const REDACT_KEY_SUFFIXES = ['email', 'phone', 'address', 'name'] as const;

// RFC-5322-ish: good enough to catch a bare address; not a validator.
const EMAIL_RE = /^[a-z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-z0-9-]+(?:\.[a-z0-9-]+)+$/i;
// `Bearer <token>` (auth header) or a connect0 `c0a_…` secret.
const TOKEN_RE = /\bbearer\s+[\w.\-+/=]+/i;
const C0A_RE = /\bc0a_[\w-]{4,}/i;

function normaliseKey(key: string): string {
  return key.toLowerCase().replace(/[^a-z0-9]/g, '');
}

function isDropKey(key: string): boolean {
  const n = normaliseKey(key);
  if (n === 'token' || n.endsWith('token')) return true;
  return DROP_KEY_SUBSTRINGS.some((sub) => n.includes(sub));
}

function isRedactKey(key: string): boolean {
  const n = normaliseKey(key);
  return REDACT_KEY_SUFFIXES.some((suf) => n === suf || n.endsWith(suf));
}

function isTokenShape(value: string): boolean {
  return TOKEN_RE.test(value) || C0A_RE.test(value);
}

function luhnValid(digits: string): boolean {
  let sum = 0;
  let dbl = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    let d = digits.charCodeAt(i) - 48;
    if (dbl) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
    dbl = !dbl;
  }
  return sum % 10 === 0;
}

function isCreditCard(value: string): boolean {
  const digits = value.replace(/[\s-]/g, '');
  if (!/^\d{13,19}$/.test(digits)) return false;
  return luhnValid(digits);
}

interface ScrubState {
  depthExceeded: boolean;
}

function scrubString(value: string, allowPii: boolean): string {
  // Compliance carve-outs — fire even when pii()-wrapped.
  if (isTokenShape(value)) return REDACTED_TOKEN;
  if (isCreditCard(value)) return REDACTED_CC;
  if (allowPii) return value;
  if (EMAIL_RE.test(value.trim())) return REDACTED_EMAIL;
  return value;
}

function scrubValue(value: unknown, key: string, allowPii: boolean, depth: number, state: ScrubState): unknown {
  if (isPii(value)) {
    // pii() overrides key/email rules for its (scalar) payload, but not
    // the compliance carve-outs; the marker does not propagate into
    // nested objects.
    return scrubValue(unwrapPii(value), key, true, depth, state);
  }

  if (typeof value === 'string') {
    const scrubbed = scrubString(value, allowPii);
    if (scrubbed !== value) return scrubbed;
    if (!allowPii && isRedactKey(key)) return REDACTED;
    return value;
  }

  if (value === null || typeof value !== 'object') {
    // number / boolean / bigint / undefined
    if (!allowPii && isRedactKey(key) && value !== undefined) return REDACTED;
    return value;
  }

  if (depth >= MAX_DEPTH) {
    state.depthExceeded = true;
    return REDACTED_DEPTH;
  }

  if (Array.isArray(value)) {
    return value.map((el) => scrubValue(el, key, allowPii, depth + 1, state));
  }

  return scrubRecord(value as Attributes, depth + 1, state);
}

function scrubRecord(record: Attributes, depth: number, state: ScrubState): Attributes {
  const out: Attributes = {};
  for (const [key, value] of Object.entries(record)) {
    if (isDropKey(key)) continue; // rule 5 — entry vanishes entirely
    out[key] = scrubValue(value, key, false, depth, state);
  }
  return out;
}

export interface ScrubResult {
  attributes: Attributes;
  depthExceeded: boolean;
}

/**
 * Scrub an attribute bag per §8.1. Pure — the caller decides whether to
 * emit the `redaction_depth_exceeded` warning when `depthExceeded` is set.
 */
export function scrubAttributes(attributes: Attributes | undefined): ScrubResult {
  if (!attributes) return { attributes: {}, depthExceeded: false };
  const state: ScrubState = { depthExceeded: false };
  const scrubbed = scrubRecord(attributes, 0, state);
  return { attributes: scrubbed, depthExceeded: state.depthExceeded };
}
